import { BuildingsIcon } from '@phosphor-icons/react'
import { toZonedTime } from 'date-fns-tz'
import { TFunction } from 'i18next'
import { z } from 'zod'

import { getPersonColumn, PersonColumnSettings } from '@/components/business/person-column'
import { Badge } from '@/components/ui/badge'
import { Icon } from '@/components/ui/icon'
import { DEFAULT_LIMIT } from '@/components/ui/pagination/pagination.constants'
import { getColumnId, SortOrderSchema, TableColumn } from '@/components/ui/table'
import { Tooltip } from '@/components/ui/tooltip'
import { Typography } from '@/components/ui/typography'
import { EMPTY_VALUE } from '@/constants'
import { MaintenancePriorityEnum, MaintenanceRequest } from '@/models/features'
import { formatDateOnly, formatDateTimeInTimezone } from '@/utils/date'

import { RequestObjectCell } from './components/request-object-cell'
import * as S from './maintenance-dashboard.styled'
import {
  getMaintenanceAssigneePerson,
  getMaintenanceCreatorPerson,
  getMaintenanceDescription,
  getMaintenancePriorityColor,
  getMaintenancePriorityLabel,
  getMaintenanceStatusColor,
  getMaintenanceStatusLabel,
  getMaintenanceUnit,
  isMaintenanceCreatedByPms,
  MAINTENANCE_DESCRIPTION_MAX_LENGTH,
  MAINTENANCE_HIDDEN_COLUMNS,
  MaintenancePmsType,
  MaintenanceStatusFilterEnum,
  truncateAtWord,
} from './maintenance-dashboard.utils'

export const MAINTENANCE_DASHBOARD_ID = 'maintenance-dashboard'
export const ADDITIONAL_FILTERS_DROPDOWN_WIDTH = '32.8rem'

export const getMaintenancePriorityOptions = (t: TFunction<['maintenance']>) =>
  MaintenancePriorityEnum.options.map((priority) => ({
    label: getMaintenancePriorityLabel(priority, t),
    value: priority,
  }))

interface GetMaintenanceColumnsParams {
  t: TFunction<['maintenance']>
  timezone: string
  pmsType: MaintenancePmsType
  createdByColumnSettings: PersonColumnSettings
  assigneeColumnSettings: PersonColumnSettings
}

// Note: the order is fixed for every building (EL-15257) - the PMS only decides which columns
// start hidden, see MAINTENANCE_HIDDEN_COLUMNS.
export const getMaintenanceColumns = ({
  t,
  timezone,
  pmsType,
  createdByColumnSettings,
  assigneeColumnSettings,
}: GetMaintenanceColumnsParams): TableColumn<MaintenanceRequest>[] => {
  const hiddenColumnIds = MAINTENANCE_HIDDEN_COLUMNS[pmsType]

  const columns: TableColumn<MaintenanceRequest>[] = [
    {
      id: 'idNumber',
      render: (request) => request.id,
      title: t('maintenance:requests.columns.idNumber'),
      width: '12rem',
    },
    {
      id: 'requestType',
      // No request type is persisted, so infer it: the create forms send `clientId` when a
      // resident is picked and `unit` only when there's none, so either one means "Unit".
      render: (request) =>
        request.clientId || getMaintenanceUnit(request)
          ? t('maintenance:form.requestDetails.unitOption')
          : t('maintenance:form.requestDetails.commonAreaOption'),
      title: t('maintenance:requests.columns.requestType'),
      width: '14rem',
    },
    getPersonColumn<MaintenanceRequest>(createdByColumnSettings, {
      getDisplay: (row) => row.createdByDisplay,
      getPerson: getMaintenanceCreatorPerson,
      id: 'createdBy',
      pageName: t('maintenance:requests.pageName'),
      renderFallback: (request) =>
        isMaintenanceCreatedByPms(request) ? (
          <S.CellWithAvatar>
            <Tooltip
              trigger={
                <S.PmsBadge>
                  <Icon icon={BuildingsIcon} size="1.6rem" />
                </S.PmsBadge>
              }
            >
              {t('maintenance:requests.cells.pmsIntegration')}
            </Tooltip>
            <Typography as="span" size="text-small" color="black800">
              {t('maintenance:requests.cells.pms')}
            </Typography>
          </S.CellWithAvatar>
        ) : (
          EMPTY_VALUE
        ),
      source: 'maintenance',
      title: t('maintenance:requests.columns.createdBy'),
    }),
    {
      id: 'category',
      render: (request) => request.category || EMPTY_VALUE,
      title: t('maintenance:requests.columns.category'),
      width: '16rem',
    },
    {
      id: 'requestObject',
      render: (request) => <RequestObjectCell request={request} />,
      title: t('maintenance:requests.columns.requestObject'),
      width: '22rem',
    },
    {
      // Note: listed for every building, Non-PMS included - natively managed requests just
      // never carry a priority, so they fall through to EMPTY_VALUE.
      id: 'priority',
      render: (request) =>
        request.priority ? (
          <Badge color={getMaintenancePriorityColor(request.priority)} variant="light" size="small">
            {getMaintenancePriorityLabel(request.priority, t)}
          </Badge>
        ) : (
          EMPTY_VALUE
        ),
      title: t('maintenance:requests.columns.priority'),
    },
    {
      id: 'scheduledDate',
      render: (request) =>
        request.scheduledDate
          ? formatDateTimeInTimezone(request.scheduledDate, timezone)
          : EMPTY_VALUE,
      title: t('maintenance:requests.columns.scheduledDate'),
      width: '16rem',
    },
    getPersonColumn<MaintenanceRequest>(assigneeColumnSettings, {
      getDisplay: (row) => row.assigneeDisplay,
      getPerson: getMaintenanceAssigneePerson,
      id: 'assignee',
      pageName: t('maintenance:requests.pageName'),
      source: 'maintenance',
      title: t('maintenance:requests.columns.assignee'),
    }),
    {
      id: 'status',
      render: (request) => (
        <Badge color={getMaintenanceStatusColor(request.status)} variant="light" size="small">
          {getMaintenanceStatusLabel(request.status, t)}
        </Badge>
      ),
      title: t('maintenance:requests.columns.status'),
      width: '14rem',
    },
    {
      id: 'address',
      render: (request) => {
        const { buildingId: building, clientId: client } = request
        const secondLine = client ? `#${client.apartment}, ${client.street}` : building.address
        return (
          <S.CellTextWrapper>
            <Typography as="span" size="text-small" color="black800" breakLines={1}>
              {building.name}
              {client?.yardiPropertyId ? ` (${client.yardiPropertyId})` : ''}
            </Typography>
            <Typography as="span" size="text-small" color="black400" breakLines={1}>
              {secondLine}
            </Typography>
          </S.CellTextWrapper>
        )
      },
      title: t('maintenance:requests.columns.address'),
      width: '20rem',
    },
    {
      id: 'description',
      render: (request) => {
        const description = getMaintenanceDescription(request)
        if (!description) return EMPTY_VALUE

        // Note: a plain string gets TableCell's own overflow tooltip, which already shows the
        // full text - only a shortened value needs an explicit tooltip with the original.
        const truncated = truncateAtWord(description, MAINTENANCE_DESCRIPTION_MAX_LENGTH)
        if (truncated === description) return description

        return (
          <Tooltip
            trigger={
              <Typography as="span" size="text-small" color="black800" breakLines={1}>
                {truncated}
              </Typography>
            }
          >
            {description}
          </Tooltip>
        )
      },
      title: t('maintenance:requests.columns.description'),
      width: '24rem',
    },
    {
      id: 'updatedOn',
      render: (request) => formatDateOnly(toZonedTime(request.updatedAt, timezone)),
      sortKey: 'updatedAt',
      title: t('maintenance:requests.columns.updatedOn'),
      width: '12rem',
    },
  ]

  return columns.map((column) => ({
    ...column,
    hiddenByDefault: hiddenColumnIds.includes(getColumnId(column)),
  }))
}

export const maintenanceFiltersSchema = z.object({
  clientId: z.array(z.coerce.number()).optional(),
  fromDate: z.coerce.number().optional(),
  limit: z.coerce.number().optional(),
  priority: z.array(MaintenancePriorityEnum).optional(),
  search: z.coerce.string().optional(),
  skip: z.coerce.number().optional(),
  sortBy: z.string().optional(),
  sortOrder: SortOrderSchema.optional(),
  // Note: falls back to "all" for anything else, e.g. the multi-value `status` / `type` params
  // from before EL-15063, rather than failing the whole parse and resetting every other filter.
  status: MaintenanceStatusFilterEnum.catch('all').optional(),
  toDate: z.coerce.number().optional(),
  yardiPropertyId: z.array(z.string()).optional(),
})

export type MaintenanceFilters = z.infer<typeof maintenanceFiltersSchema>

export const initialMaintenanceFilters: MaintenanceFilters = {
  clientId: [],
  fromDate: undefined,
  limit: DEFAULT_LIMIT,
  priority: [],
  search: '',
  skip: 0,
  sortBy: 'updatedAt',
  sortOrder: 'desc',
  status: 'all',
  toDate: undefined,
  yardiPropertyId: [],
}
